import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Close,
  DirectionsBus,
  Person,
  AirlineSeatReclineNormal,
  LocalActivity,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import api from '../../services/api';

interface Passenger {
  display_name: string;
  seats: number;
  is_member: boolean;
  member_status: 'member' | 'visiting_member' | 'guest';
}

interface BusSeatingModalProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  shuttleName?: string;
  departureDate?: string;
  departureTime?: string;
  pickupLocation?: string;
  destination?: string;
  currentBooking?: {
    guest_nickname?: string;
    guest_name?: string;
    seats_booked?: number;
  };
}

const BusSeatingModal: React.FC<BusSeatingModalProps> = ({
  open,
  onClose,
  scheduleId,
  shuttleName,
  departureDate,
  departureTime,
  pickupLocation,
  destination,
  currentBooking,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passengers, setPassengers] = useState<Passenger[]>([]);
  const [capacity, setCapacity] = useState(0);
  const [totalSeats, setTotalSeats] = useState(0);

  useEffect(() => {
    if (open && scheduleId) {
      fetchPassengers();
    }
  }, [open, scheduleId]);

  const fetchPassengers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/public/schedules/${scheduleId}/passengers`);
      
      const data = response.data;
      setPassengers(data.passengers || []);
      setCapacity(data.shuttle?.capacity || 20);
      setTotalSeats(data.total_booked_seats || 0);
    } catch (error: any) {
      console.error('Failed to fetch passengers:', error);
      setError(t('shuttle.errors.fetchPassengers') || 'Failed to load passengers');
    } finally {
      setLoading(false);
    }
  };

  // Check if a passenger is the current user
  const isCurrentUserPassenger = (passenger: Passenger | null) => {
    if (!passenger || !currentBooking) return false;
    
    // Check by nickname first, then by name
    const passengerName = passenger.display_name.toLowerCase();
    const currentNickname = currentBooking.guest_nickname?.toLowerCase();
    const currentName = currentBooking.guest_name?.toLowerCase();
    
    return (currentNickname && passengerName.includes(currentNickname)) ||
           (currentName && passengerName.includes(currentName)) ||
           passengerName === currentNickname ||
           passengerName === currentName;
  };

  // Create seat layout - typical shuttle bus has 4 seats per row
  const createSeatLayout = () => {
    const seats = [];
    // Expand passengers to individual seats
    const expandedSeats: Array<{passenger: Passenger, seatIndex: number}> = [];
    
    passengers.forEach(passenger => {
      const seatsToBook = passenger.seats || 1;
      for (let i = 0; i < seatsToBook; i++) {
        expandedSeats.push({ passenger, seatIndex: i });
      }
    });
    
    const seatQueue = [...expandedSeats];
    const rows = Math.ceil(capacity / 4);
    
    for (let row = 0; row < rows; row++) {
      const rowSeats = [];
      
      // Left side seats (2 seats)
      for (let col = 0; col < 2; col++) {
        const seatNumber = row * 4 + col + 1;
        if (seatNumber <= capacity) {
          const seatData = seatQueue.length > 0 ? seatQueue.shift() || null : null;
          const isCurrentUser = seatData ? isCurrentUserPassenger(seatData.passenger) : false;
          
          rowSeats.push({
            seatNumber,
            passenger: seatData?.passenger || null,
            seatIndex: seatData?.seatIndex || 0,
            side: 'left',
            isCurrentUserSeat: isCurrentUser,
          });
        }
      }
      
      // Aisle space
      rowSeats.push({ aisle: true });
      
      // Right side seats (2 seats)
      for (let col = 0; col < 2; col++) {
        const seatNumber = row * 4 + col + 3;
        if (seatNumber <= capacity) {
          const seatData = seatQueue.length > 0 ? seatQueue.shift() || null : null;
          const isCurrentUser = seatData ? isCurrentUserPassenger(seatData.passenger) : false;
          
          rowSeats.push({
            seatNumber,
            passenger: seatData?.passenger || null,
            seatIndex: seatData?.seatIndex || 0,
            side: 'right',
            isCurrentUserSeat: isCurrentUser,
          });
        }
      }
      
      seats.push(rowSeats);
    }
    
    return seats;
  };

  const getSeatColor = (passenger: Passenger | null) => {
    if (!passenger) return '#e0e0e0'; // Empty seat
    
    switch (passenger.member_status) {
      case 'member':
        return '#4caf50'; // Green for members
      case 'visiting_member':
        return '#ff9800'; // Orange for visiting members
      case 'guest':
        return '#2196f3'; // Blue for guests
      default:
        return '#9e9e9e';
    }
  };

  const formatDate = () => {
    if (!departureDate) return '';
    try {
      const date = new Date(departureDate);
      return format(date, 'PPP');
    } catch {
      return departureDate;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '600px',
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DirectionsBus sx={{ color: 'primary.main' }} />
            <Typography variant="h6">
              {shuttleName || t('shuttle.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        {(departureDate || departureTime || pickupLocation || destination) && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {formatDate()} {departureTime && `at ${departureTime}`}
            </Typography>
            {(pickupLocation || destination) && (
              <Typography variant="body2" color="text.secondary">
                {pickupLocation || t('shuttle.defaultPickup')} → {destination || t('shuttle.defaultDestination')}
              </Typography>
            )}
          </Box>
        )}
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Stats Bar */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<Person />}
                label={`${passengers.length} ${t('shuttle.passengers.list')}`}
                color="primary"
                variant="outlined"
              />
              <Chip
                icon={<AirlineSeatReclineNormal />}
                label={`${totalSeats}/${capacity} ${t('shuttle.seats')}`}
                color={totalSeats >= capacity ? 'error' : 'success'}
                variant="outlined"
              />
              <Chip
                icon={<LocalActivity />}
                label={`${capacity - totalSeats} ${t('shuttle.seatsAvailable')}`}
                variant="outlined"
              />
            </Box>

            {/* Legend */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              {currentBooking && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    bgcolor: '#f5f5f5', 
                    border: '3px solid #ff4444',
                    borderRadius: 1,
                    position: 'relative',
                  }}>
                    <Typography sx={{ 
                      position: 'absolute', 
                      top: -8, 
                      right: -8, 
                      color: '#ff4444',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}>
                      ★
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#ff4444' }}>
                    {t('shuttle.passengers.yourSeats', 'Your Seats')} ({currentBooking.seats_booked})
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: '#4caf50', borderRadius: 1 }} />
                <Typography variant="caption">{t('shuttle.passengers.member')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: '#ff9800', borderRadius: 1 }} />
                <Typography variant="caption">{t('shuttle.passengers.visitingMember')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: '#2196f3', borderRadius: 1 }} />
                <Typography variant="caption">{t('shuttle.passengers.guest')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: '#e0e0e0', borderRadius: 1 }} />
                <Typography variant="caption">{t('shuttle.passengers.emptySeat', 'Empty')}</Typography>
              </Box>
            </Box>

            {/* Bus Layout */}
            <Paper
              elevation={3}
              sx={{
                p: 3,
                bgcolor: '#f5f5f5',
                borderRadius: 2,
                maxWidth: '500px',
                margin: '0 auto',
              }}
            >
              {/* Driver Section */}
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  bgcolor: '#333',
                  color: 'white',
                  borderRadius: '8px 8px 0 0',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption">
                  {t('shuttle.driver', 'Driver')}
                </Typography>
              </Box>

              {/* Seats */}
              <Box sx={{ px: 2 }}>
                {createSeatLayout().map((row, rowIndex) => (
                  <Box
                    key={rowIndex}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 1,
                      justifyContent: 'center',
                    }}
                  >
                    {row.map((seat: any, seatIndex) => {
                      if (seat.aisle) {
                        return (
                          <Box
                            key={`aisle-${rowIndex}-${seatIndex}`}
                            sx={{ width: 30 }}
                          />
                        );
                      }

                      const isOccupied = seat.passenger !== null;
                      const isCurrentUserSeat = seat.isCurrentUserSeat;
                      const seatColor = getSeatColor(seat.passenger);
                      const isFirstSeatOfPassenger = seat.seatIndex === 0;

                      return (
                        <Tooltip
                          key={`seat-${seat.seatNumber}`}
                          title={
                            isOccupied
                              ? `${seat.passenger.display_name}${
                                  seat.passenger.seats > 1 
                                    ? ` (Seat ${seat.seatIndex + 1}/${seat.passenger.seats})` 
                                    : ''
                                }${isCurrentUserSeat ? ' - Your booking' : ''}`
                              : t('shuttle.passengers.emptySeat', 'Empty seat')
                          }
                          placement="top"
                        >
                          <Box
                            sx={{
                              width: 50,
                              height: 50,
                              bgcolor: seatColor,
                              borderRadius: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: isCurrentUserSeat ? '3px solid' : '2px solid',
                              borderColor: isCurrentUserSeat 
                                ? '#ff4444' // Red border for current user's seats
                                : isOccupied ? 'rgba(0,0,0,0.2)' : '#bdbdbd',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              position: 'relative',
                              boxShadow: isCurrentUserSeat ? '0 0 8px rgba(255,68,68,0.5)' : 'none',
                              '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: isCurrentUserSeat 
                                  ? '0 0 12px rgba(255,68,68,0.7)' 
                                  : 2,
                              },
                              '&::before': isCurrentUserSeat ? {
                                content: '"★"',
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                color: '#ff4444',
                                fontSize: '16px',
                                fontWeight: 'bold',
                              } : {},
                            }}
                          >
                            {isOccupied ? (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    color: 'white',
                                    textAlign: 'center',
                                    lineHeight: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: '90%',
                                  }}
                                >
                                  {seat.passenger.display_name.split(' ')[0]}
                                </Typography>
                                {seat.passenger.seats > 1 && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '9px',
                                      color: 'white',
                                      opacity: 0.9,
                                    }}
                                  >
                                    {seat.seatIndex + 1}/{seat.passenger.seats}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '10px',
                                  color: 'text.disabled',
                                }}
                              >
                                {seat.seatNumber}
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                ))}
              </Box>

              {/* Back of Bus */}
              <Box
                sx={{
                  mt: 2,
                  p: 1,
                  bgcolor: '#666',
                  borderRadius: '0 0 8px 8px',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: 'white' }}>
                  {t('shuttle.passengers.backOfBus', 'Back')}
                </Typography>
              </Box>
            </Paper>

            {/* Passenger List */}
            {passengers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 2, mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('shuttle.passengers.noPassengers')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('shuttle.passengers.list')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {passengers.map((passenger, index) => (
                    <Chip
                      key={index}
                      label={`${passenger.display_name} (${passenger.seats})`}
                      size="small"
                      sx={{
                        bgcolor: getSeatColor(passenger),
                        color: 'white',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BusSeatingModal;